import http from './base/http';
import Config from '@/config';
import {
  Zodiac,
  CURRENCY_DEFAULT,
  LOCALE_CODE_DEFAULT,
} from '@/helper/constants';
import { coustomInterval } from '@/helper/util';
import { AddonsLog } from '@/helper/log';
import { handleAACheckoutError } from '@/helper/error-handler';

const userContext = {
  currency: CURRENCY_DEFAULT,
  geoId: Zodiac.GEO_ID,
  locale: LOCALE_CODE_DEFAULT,
};

export const checkPriceWithoutPolling = async <T>(
  tripId,
  airlineProfile,
  tokenInfo: TokensExternalAuth,
) => {
  const url = `${Config.ZODIAC.SERVICE_ENDPOINT}/fp/checkout/v3/page-data`;

  const headers = {
    channel_hash: Config.ZODIAC.CHANNEL_HASH,
    'Ga-Id': generateGaId(tokenInfo.userId),
  };

  const data = {
    userContext,
    flightDetails: {
      tripId: [`${tripId}`],
      airlineProfile: airlineProfile,
    },
    validateFlights: false,
    sso: {
      accessToken: tokenInfo.accessToken,
      refreshToken: tokenInfo.refreshToken,
      id: tokenInfo.userId,
    },
  };

  return http.post<T>(url, data, { headers });
};

export const checkPrice = async <T>(
  tripId,
  airlineProfile,
  tokenInfo: TokensExternalAuth,
) => {
  let count = 0;
  let isValid = false;
  let pageData = null;
  const polling = async (stop) => {
    pageData = await checkPriceWithoutPolling(
      tripId,
      airlineProfile,
      tokenInfo,
    );
    count++;

    if (
      pageData.flightDetails &&
      pageData.flightDetails.requiresValidation === false
    ) {
      isValid = true;
      stop();
    } else if (count == Zodiac.MAX_COUNT_POLLING_CHECKP_PRICE) {
      stop();
    }

    return pageData;
  };
  await coustomInterval(polling, Zodiac.GAP_POLLING_CHECKP_PRICE).start();

  return {
    isValid,
    pageData,
  };
};

export const generateGaId = (userId) => `GA-WMP-${userId}`;

export const checkoutV3 = async <T>(data) => {
  const url = `${Config.ZODIAC.SERVICE_ENDPOINT}/fp/checkout/v3/initiate`;
  const userId = data.loggedInUserInfo?.ssoDetails?.userId;

  const headers = {
    channel_hash: Config.ZODIAC.CHANNEL_HASH,
    'user-type': Zodiac.USER_TYPE_MEMBER,
    'Ga-Id': generateGaId(userId),
  };

  try {
    const res = await http.post<T>(url, data, { headers });
    AddonsLog.logCheckoutSuccess({ url, headers, data }, { data: res });
    return res;
  } catch (error) {
    AddonsLog.logCheckoutFailure({ url, headers, data }, error?.response);
    handleAACheckoutError(error);
  }
};

export const checkout = async <T>(data) => {
  const url = `${Config.ZODIAC.SERVICE_ENDPOINT}/fp/checkout/v1/initiate`;

  const headers = {
    channel_hash: Config.ZODIAC.CHANNEL_HASH,
  };

  return http.post<T>(url, data, { headers });
};

const getAncillaryRequestData = (
  data: {
    tripsInfo: any;
    tokenInfo: TokensExternalAuth;
  },
  includeAncillary: Array<string>,
) => {
  const { tripsInfo, tokenInfo } = data;
  return {
    userContext,
    flightDetails: {
      tripId: tripsInfo.tripIds,
      airlineProfile: tripsInfo.airlineProfile,
      departCabinType: tripsInfo.departCabinClass,
      returnCabinType: tripsInfo.returnCabinClass,
    },
    validateFlights: false,
    sso: {
      accessToken: tokenInfo.accessToken,
      refreshToken: tokenInfo.refreshToken,
      userId: tokenInfo.userId,
    },
    includeAncillary,
  };
};

export const getAncillaryItem = async <T>(
  data: {
    tripsInfo: any;
    tokenInfo: TokensExternalAuth;
  },
  items: string[] = [],
  includeList: string[] = ['ancillary', 'locale'],
  customFlag?: string,
) => {
  const { tokenInfo } = data;
  const includeListStr =
    includeList.length > 0 ? `?include_list=${includeList.join(',')}` : '';
  const url = `${Config.ZODIAC.SERVICE_ENDPOINT}/fp/checkout/v4/page-data${includeListStr}`;

  const headers = {
    channel_hash: Config.ZODIAC.CHANNEL_HASH,
    'user-type': Zodiac.USER_TYPE_MEMBER,
    'Ga-Id': generateGaId(tokenInfo.userId),
  };

  const requestData = getAncillaryRequestData(data, items);
  const config: any = { headers };

  // extract request flag from items[0] or use custom flag
  const requestFlag = customFlag || items[0];
  config._requestFlag = requestFlag;

  const isFlightDetail = items.length === 0;
  try {
    const res = await http.post<T>(url, requestData, config);

    const flightDetailsHasError = !res.flightDetails || res.flightDetails.error;

    if (isFlightDetail && !flightDetailsHasError) {
      AddonsLog.logFlightDetailsSuccess(
        { url, headers, data: requestData },
        { data: res },
      );
    } else if (isFlightDetail && flightDetailsHasError) {
      AddonsLog.logFlightDetailsFailure(
        { url, headers, data: requestData },
        { data: res },
      );
    }

    return res;
  } catch (error) {
    if (isFlightDetail) {
      AddonsLog.logFlightDetailsFailure(
        { url, headers, data: requestData },
        error?.response,
      );
    }

    throw error;
  }
};

export const getAncillaryBaggage = async <T>(data) => {
  return getAncillaryItem<T>(data, ['baggage']);
};

export const getAncillaryInsurance = async <T>(data) => {
  return getAncillaryItem<T>(data, ['insurance']);
};

export const getAncillaryWheelchair = async <T>(data) => {
  return getAncillaryItem<T>(data, ['wheelchair']);
};

export const getAncillaryBundle = async <T>(data) => {
  return getAncillaryItem<T>(data, ['bundle']);
};

export const getAncillaryFastPass = async <T>(data) => {
  return getAncillaryItem<T>(data, ['fastPass', 'immigrationFastLane']);
};

export const getAncillaryMeal = async <T>(data) => {
  return getAncillaryItem<T>(data, ['meal']);
};

export const getAncillarySeat = async <T>(data) => {
  return getAncillaryItem<T>(data, ['seat']);
};

export const getPageData = async <T>(data: any) => {
  return getAncillaryItem<T>(data, [], [], 'flightDetails');
};
